import { datadogRum } from '@datadog/browser-rum'
import { AnyAction } from '@reduxjs/toolkit'
import { captureException } from '@sentry/browser'
import i18n from 'i18next'
import get from 'lodash/get'
import hash from 'object-hash'
import { parse, stringify } from 'zipson'

import { ContactUsLink } from 'components/Links'
import { signOut } from 'features/Auth/service/signOut'
import { updateCache } from 'reducers/cache'
import { selectDomainValue as selectAuthDomainValue } from 'selectors/auth'
import { selectCache } from 'selectors/cache'
import { RootReduxState } from 'types'
import message from 'utilities/message'
import storeRegistry from 'utilities/storeRegistry'

interface CacheParams {
    organizationId: string | null
    organizationGroupId: string | null
    apiParams: any
}

const WHITE_LISTED_ERROR_MESSAGES = [
    'There is already a matching download running. Please wait for it to end before starting a new one',
]

const shouldUseCache = (apiFunc: any): boolean => apiFunc.cache === true

const formatErrorMessage = (data: any): string => {
    if (typeof data === 'string') {
        return data
    }
    if (Array.isArray(data)) {
        return data.map(formatErrorMessage).join(', ')
    }
    return JSON.stringify(data)
}

const constructCacheParams = (apiParams: any): string => {
    const state = storeRegistry.getState() as RootReduxState
    const cacheParams: CacheParams = {
        organizationId: selectAuthDomainValue(state, 'organizationId'),
        organizationGroupId: selectAuthDomainValue(
            state,
            'organizationGroupId'
        ),
        apiParams,
    }
    return hash(cacheParams)
}

type SuccessActionFunc =
    | ((...args: any[]) => AnyAction)
    | ((...args: any[]) => any)
    | null

/**
 * Runs a Cerebro API request with optional response caching, dispatches
 * `successActionFunc(data)` on 2xx, signs the user out on 401, and
 * captures/rethrows other errors. Used by the auth and app services.
 *
 * `shouldCommit` skips the Redux success/cache commit when it returns false at
 * the moment the response resolves. The app-bootstrap and org-switch flows pass
 * it so a superseded context (a rapid second org/group switch) cannot write its
 * stale, out-of-context results over the latest one. The 401 → sign-out and
 * error paths are never gated. An `undefined` guard always commits.
 */
export async function cerebroApiRequestGuarded(
    shouldCommit: (() => boolean) | undefined,
    successActionFunc: SuccessActionFunc,
    apiFunc: any,
    ...apiParams: any[]
): Promise<any> {
    let response: any
    let error: any

    const funcName = apiFunc.name
    const canCommit = (): boolean => !shouldCommit || shouldCommit()

    let cacheParams: string | null = null
    if (shouldUseCache(apiFunc)) {
        cacheParams = constructCacheParams(apiParams)
        const cachedData: any = selectCache(
            storeRegistry.getState() as RootReduxState,
            [funcName, cacheParams]
        )
        if (cachedData !== undefined) {
            const parsedData = parse(cachedData)
            if (successActionFunc && canCommit()) {
                storeRegistry.dispatch(successActionFunc(parsedData))
            }
            return { status: 200, data: parsedData }
        }
    }

    try {
        response = await apiFunc(...apiParams)
        const { status, data } = response

        if (status >= 200 && status < 300) {
            // Decide once so the success dispatch and cache write commit (or
            // skip) together — never a partial commit.
            const committed = canCommit()
            if (successActionFunc && committed) {
                storeRegistry.dispatch(successActionFunc(data))
            }

            if (shouldUseCache(apiFunc) && committed) {
                storeRegistry.dispatch(
                    updateCache({
                        funcName,
                        params: cacheParams,
                        data: stringify(data, { fullPrecisionFloats: true }),
                    })
                )
            }
        } else if (status === 400) {
            error = new Error(formatErrorMessage(data))
        } else if (status === 401) {
            const sessionExpiredMessage = i18n.t(
                'common:auth.sessionExpired',
                'Your session has expired. Please log in.'
            )
            message.warning({
                content: sessionExpiredMessage,
                key: 'sessionExpired',
            })
            await signOut()
        } else if (status === 403) {
            const notAuthorizedMessage = i18n.t(
                'common:auth.notAuthorized',
                'Not authorized.'
            )
            error = new Error(notAuthorizedMessage)
        } else if (status === 404 || status === 429) {
            // Do nothing if api is throttled or resource not found
        } else {
            const requestFailedStatusMessage = i18n.t(
                'common:auth.requestFailed.status',
                'Request failed (status code {{ status }}).',
                { status }
            )
            error = new Error(requestFailedStatusMessage)
        }
    } catch (err) {
        error = err
    }

    if (error) {
        const errorMessage = get(error, 'message')
        const errorStatus =
            get(response, 'status') || get(error, ['response', 'status'])

        if (errorStatus === 500) {
            message.error(
                <>
                    Something went wrong and we&apos;ve already been notified.
                    Please try again or{' '}
                    <ContactUsLink authenticated={false} link>
                        contact us
                    </ContactUsLink>{' '}
                    if this issue continues.
                </>
            )
        } else if (errorMessage && errorStatus >= 400) {
            message.error(errorMessage)
        }

        if (
            errorStatus !== 400 ||
            !WHITE_LISTED_ERROR_MESSAGES.includes(errorMessage)
        ) {
            console.error({ error, errorStatus })
            captureException(error)
            datadogRum.addError(error)
            throw error
        }
    }

    return response
}

/**
 * Unguarded `cerebroApiRequestGuarded`: always commits. The default request
 * helper for callers that don't participate in a bootstrap/org-switch context.
 */
export async function cerebroApiRequest(
    successActionFunc: SuccessActionFunc,
    apiFunc: any,
    ...apiParams: any[]
): Promise<any> {
    return cerebroApiRequestGuarded(
        undefined,
        successActionFunc,
        apiFunc,
        ...apiParams
    )
}
